import React, { useState } from 'react';
import {
  Card, CardContent, Typography, Button, LinearProgress,
  Box, Alert, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import { PlayArrow, DeleteOutline, Download } from '@mui/icons-material';
import { CSVProcessor } from '../../utils/csvProcessor';
import apiClient from '../../utils/apiClient';
import { toast } from 'react-toastify';
import JSZip from 'jszip';

const DemigrationPanel = ({ file }) => {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle');
  const [results, setResults] = useState({ success: 0, failed: 0 });
  const [totalRecords, setTotalRecords] = useState(0);
  const [batches, setBatches] = useState([]);
  const [showBatches, setShowBatches] = useState(false);

  const handleSplitFile = async () => {
    console.log('🚀 Split button clicked for demigration, File:', file.name);
    try {
      const fileBatches = await CSVProcessor.splitCSV(file, 100, 1);
      
      // Read first batch to show sample data
      if (fileBatches.length > 0) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const csvContent = e.target.result;
          const lines = csvContent.split('\n');
          console.log('📊 SAMPLE - First batch headers:', lines[0]);
          console.log('📊 SAMPLE - First 5 rows with ROWNO:');
          lines.slice(1, 6).forEach((line, idx) => {
            console.log(`Row ${idx + 1}:`, line);
          });
        };
        reader.readAsText(fileBatches[0].data);
      }
      
      const batchesWithStatus = fileBatches.map((batch, index) => ({
        ...batch,
        id: index + 1,
        status: 'pending',
        error: null,
        startRow: 1 + (index * 100),
        endRow: 1 + (index * 100) + batch.rowCount - 1
      }));
      setBatches(batchesWithStatus);
      setTotalRecords(batchesWithStatus.reduce((sum, batch) => sum + batch.rowCount, 0));
      console.log('✅ Split complete! Total batches:', batchesWithStatus.length, 'Total records:', batchesWithStatus.reduce((sum, batch) => sum + batch.rowCount, 0));
      setShowBatches(true);
      setStatus('split');
    } catch (error) {
      console.error('❌ Split failed:', error);
      setStatus('error');
    }
  };

  const handleSingleBatch = async (batchIndex) => {
    const batch = batches[batchIndex];
    if (batch.status === 'processing') return;

    console.log('🚀 Processing demigration batch:', batch.id);
    
    // Read and log the CSV payload
    const reader = new FileReader();
    reader.onload = (e) => {
      const csvContent = e.target.result;
      console.log('📋 PAYLOAD - Batch', batch.id, 'CSV Content:');
      console.log(csvContent);
      const lines = csvContent.split('\n');
      console.log('📊 PAYLOAD - Headers:', lines[0]);
      console.log('📊 PAYLOAD - First 3 rows:', lines.slice(1, 4));
      console.log('📊 PAYLOAD - Total rows in batch:', lines.length - 1);
    };
    reader.readAsText(batch.data);
    
    setBatches(prev => prev.map((b, i) => 
      i === batchIndex ? { ...b, status: 'processing' } : b
    ));

    try {
      const response = await apiClient.uploadBatch(batch.data, '/api/v1/demigrateTransactions/');
      console.log('✅ Batch', batch.id, 'success:', response);
      setBatches(prev => prev.map((b, i) => 
        i === batchIndex ? { ...b, status: 'success' } : b
      ));
      setResults(prev => ({ ...prev, success: prev.success + 1 }));
      
      if (response.message) {
        toast.success(response.message);
      } else {
        toast.success(`Batch ${batch.id} demigrated successfully`);
      }
    } catch (error) {
      console.error('❌ Batch', batch.id, 'failed:', error);
      let errorMessage = error.message;
      if (error.message.includes('Authentication failed')) {
        errorMessage = 'Authentication failed - please login again';
      }
      setBatches(prev => prev.map((b, i) => 
        i === batchIndex ? { ...b, status: 'failed', error: errorMessage } : b
      ));
      setResults(prev => ({ ...prev, failed: prev.failed + 1 }));
      
      toast.error(`Batch ${batch.id} failed: ${errorMessage}`);
    }
  };

  const handleProcessAll = async () => {
    const pendingBatches = batches.filter(b => b.status === 'pending' || b.status === 'failed');
    setStatus('processing');
    
    for (let i = 0; i < pendingBatches.length; i++) {
      const batchIndex = batches.findIndex(b => b.id === pendingBatches[i].id);
      await handleSingleBatch(batchIndex);
      
      const progressPercent = ((i + 1) / pendingBatches.length) * 100;
      setProgress(progressPercent);
    }
    
    setStatus('completed');
  };

  const downloadBatches = async (statusFilter) => {
    const filteredBatches = batches.filter(b => b.status === statusFilter);
    
    if (filteredBatches.length === 0) {
      toast.info(`No ${statusFilter} batches to download`);
      return;
    }

    const zip = new JSZip();
    const folderName = statusFilter === 'success' ? 'successful_batches' : 'failed_batches';
    const folder = zip.folder(folderName);

    for (const batch of filteredBatches) {
      const fileName = `batch_${batch.id}.csv`;
      folder.file(fileName, batch.data);
    }

    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${file.name.replace('.csv', '')}_${folderName}.zip`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success(`Downloaded ${filteredBatches.length} ${statusFilter} batches`);
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {file.name}
        </Typography>
        
        <Button
          variant="outlined"
          onClick={handleSplitFile}
          disabled={status === 'processing'}
          startIcon={<DeleteOutline />}
          sx={{ mb: 2 }}
          color="error"
        >
          Split for Demigration
        </Button>

        {showBatches && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleProcessAll}
                startIcon={<PlayArrow />}
                color="error"
              >
                Process All Pending
              </Button>
              <Button
                variant="outlined"
                color="success"
                onClick={() => downloadBatches('success')}
                startIcon={<Download />}
                disabled={results.success === 0}
              >
                Download Success ({results.success})
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => downloadBatches('failed')}
                startIcon={<Download />}
                disabled={results.failed === 0}
              >
                Download Failed ({results.failed})
              </Button>
              <Chip label={`Total: ${batches.length} batches`} />
            </Box>
            
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Batch #</TableCell>
                    <TableCell>Row Range (ROWNO)</TableCell>
                    <TableCell>Records</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batches.map((batch, index) => (
                    <TableRow key={batch.id}>
                      <TableCell>{batch.id}</TableCell>
                      <TableCell>
                        <Chip 
                          label={`${batch.startRow} - ${batch.endRow}`} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{batch.rowCount}</TableCell>
                      <TableCell>
                        <Chip 
                          label={batch.status} 
                          color={
                            batch.status === 'success' ? 'success' :
                            batch.status === 'failed' ? 'error' :
                            batch.status === 'processing' ? 'warning' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => handleSingleBatch(index)}
                          disabled={batch.status === 'processing' || batch.status === 'success'}
                          startIcon={<PlayArrow />}
                          color="error"
                        >
                          {batch.status === 'success' ? 'Completed' : batch.status === 'processing' ? 'Processing...' : 'Delete'}
                        </Button>
                      </TableCell>
                      <TableCell>
                        {batch.error && (
                          <Typography variant="caption" color="error">
                            {batch.error}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {status === 'processing' && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress variant="determinate" value={progress} color="error" />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Processing... {Math.round(progress)}% ({results.success + results.failed}/{batches.filter(b => b.status !== 'pending').length + batches.filter(b => b.status === 'pending' || b.status === 'failed').length} batches)
            </Typography>
          </Box>
        )}

        {status === 'split' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            File split into {batches.length} batches ({totalRecords} total records). 
            Use "Delete" buttons to demigrate individual batches or "Process All Pending" for bulk demigration.
          </Alert>
        )}

        {status === 'completed' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Demigration completed! Success: {results.success} | Failed: {results.failed}
          </Alert>
        )}

        {status === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Demigration failed. Please try again.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default DemigrationPanel;
